import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AccountService } from '../account.service';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private modules: any[] = [];
  private role: any = {};

  constructor(private accountService: AccountService) {}

  getModules(): any[] {
    return this.modules;
  }

  loadForUser(userId: any, opts?: { force?: boolean }): Observable<any> {
    return this.accountService.permissionsByUser(userId, opts).pipe(
      tap((res: any) => {
        this.modules = Array.isArray(res?.modules) ? res.modules : [];
        this.role = res.role;
      })
    );
  }

  getRole(): any {
    return this.role;
  }

  /** Limpia el estado en memoria al cerrar sesión, para que un login
   * posterior (de otro usuario/rol en la misma pestaña) no reutilice
   * permisos obsoletos. */
  reset(): void {
    this.modules = [];
    this.role = {};
  }

  /**
   * Ruta (router link segments) de la primera página que el usuario tiene
   * asociada, en el mismo orden en que aparece el menú lateral. Usa el hijo
   * cuando el primer módulo es un submenú.
   */
  getFirstAccessibleRoute(): string[] | null {
    const first = Array.isArray(this.modules) ? this.modules[0] : null;
    if (!first) return null;
    const target = Array.isArray(first.children) && first.children.length > 0 ? first.children[0] : first;
    const raw: string = target?.route ?? '';
    const segments = raw.split(/[./]/).filter(Boolean);
    if (!segments.length) return null;
    return ['/', ...segments];
  }

  canDo(moduleKey: string, actionName: string): boolean {
    if (!Array.isArray(this.modules) || !moduleKey || !actionName) return false;
    const wanted = String(actionName).toLowerCase();
    const actions = this.collectActionsByModuleName(this.modules, String(moduleKey));
    return actions.some((a: any) => String(a?.name || '').toLowerCase() === wanted);
  }

  private collectActionsByModuleName(nodes: any[], key: string): any[] {
    const collected: any[] = [];
    // Un módulo padre puede ser solo un contenedor sin ruta propia navegable
    // (su ruta real la re-expone uno de sus hijos, p. ej. "Listado de..."),
    // así que las acciones "del módulo" pueden vivir en él, en sus hijos, o
    // repartidas entre ambos. Al encontrar el módulo por nombre, se agregan
    // las acciones propias MÁS las de todos sus descendientes.
    const collectAllActions = (node: any) => {
      if (Array.isArray(node?.actions)) collected.push(...node.actions);
      if (Array.isArray(node?.children)) node.children.forEach(collectAllActions);
    };
    const visit = (arr: any[]) => {
      for (const n of arr) {
        const name = String(n?.name || '');
        if (name === key) {
          collectAllActions(n);
        } else if (Array.isArray(n?.children) && n.children.length) {
          visit(n.children);
        }
      }
    };
    visit(nodes);
    return collected;
  }
}
